"use client";

import { c as _c } from "react/compiler-runtime";
import { applyGroupHeaderCssVars, buildReplicaSearchIndex, countMegaTabEntries, lookupNavIcon, replicaForceDesktopPreview, replicaForcedWidthPx, replicaFromPayload, replicaLayoutFlags, replicaStickyShellState } from "@multi-tenant-cms/builder/ui/lib/replicaMegaMenuHeader";
import type { AdvancedMegaMenuHeaderRuntimePayload, GroupHeaderBrand, MenuRootNode } from "@multi-tenant-cms/types/menu";
import ghh from "@multi-tenant-cms/ui/styles/group-header.module.css";
import { type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isLocaleCurrentForPath, resolveLocaleCodeFromPath } from "./megaMenuLocale";
import { NavIconLabel, resolveNavIconPosition } from "./navIconSpan";
import { ReplicaMegaPanel } from "./ReplicaMegaPanel";
import { useMegaMenuStickyHeader } from "./useMegaMenuStickyHeader";
import { useReplicaBarOverflow } from "./useReplicaBarOverflow";
type ViewportMode = "mobile" | "tablet" | "desktop";
type MegaTabRoot = Extract<MenuRootNode, {
  itemKind: "mega_tab";
}>;
const MOBILE_MAX = 767;
function safeLogoHref(logoHref: string | undefined, logoUrl?: string): string {
  const href = (logoHref ?? "/").trim() || "/";
  const url = logoUrl?.trim();
  if (url && href === url) {
    return "/";
  }
  if (/\/api\/media\//i.test(href)) {
    return "/";
  }
  if (/^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i.test(href)) {
    return "/";
  }
  return href;
}
function useViewportWidth(forced, forcedWidthPx) {
  const $ = _c(6);
  let t0;
  if ($[0] !== forcedWidthPx) {
    t0 = () => forcedWidthPx ?? 1600;
    $[0] = forcedWidthPx;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [width, setWidth] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== forced || $[3] !== forcedWidthPx) {
    t1 = () => {
      if (forced === "mobile" || forced === "tablet" || forced === "desktop") {
        if (forcedWidthPx != null) {
          setWidth(forcedWidthPx);
        }
        return;
      }
      const update = () => setWidth(window.innerWidth);
      update();
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    };
    t2 = [forced, forcedWidthPx];
    $[2] = forced;
    $[3] = forcedWidthPx;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  if (forced === "mobile" || forced === "tablet" || forced === "desktop") {
    return forcedWidthPx ?? width;
  }
  return width;
}
function SearchIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} fill="none" focusable="false" height="18" role="presentation" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" width="18"><title>Search</title><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const REGION_POP_GAP_PX = 10;
function RegionPopPortal(t0) {
  const $ = _c(20);
  const {
    ariaLabel,
    children,
    host,
    onDialogClick,
    open,
    portalClassName,
    popClassName,
    triggerRef
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      top: 0,
      right: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [pos, setPos] = useState(t1);
  let t2;
  if ($[1] !== host || $[2] !== triggerRef) {
    t2 = () => {
      if (!host) {
        return;
      }
      const trigger = triggerRef.current;
      if (!trigger) {
        return;
      }
      const hostBox = host.getBoundingClientRect();
      const triggerBox = trigger.getBoundingClientRect();
      setPos({
        top: triggerBox.bottom - hostBox.top + REGION_POP_GAP_PX,
        right: hostBox.right - triggerBox.right
      });
    };
    $[1] = host;
    $[2] = triggerRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const updatePos = t2;
  let t3;
  let t4;
  if ($[4] !== open || $[5] !== updatePos) {
    t3 = () => {
      if (!open) {
        return;
      }
      updatePos();
      window.addEventListener("resize", updatePos);
      window.addEventListener("scroll", updatePos, true);
      return () => {
        window.removeEventListener("resize", updatePos);
        window.removeEventListener("scroll", updatePos, true);
      };
    };
    t4 = [open, updatePos];
    $[4] = open;
    $[5] = updatePos;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useLayoutEffect(t3, t4);
  if (!(open && host)) {
    return null;
  }
  const t5 = `${popClassName} ${ghh.open} ${portalClassName}`;
  let t6;
  if ($[8] !== pos.right || $[9] !== pos.top) {
    t6 = {
      top: pos.top,
      right: pos.right
    };
    $[8] = pos.right;
    $[9] = pos.top;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== ariaLabel || $[12] !== children || $[13] !== onDialogClick || $[14] !== t5 || $[15] !== t6) {
    t7 = <div aria-label={ariaLabel} className={t5} onClick={onDialogClick} role="dialog" style={t6}>{children}</div>;
    $[11] = ariaLabel;
    $[12] = children;
    $[13] = onDialogClick;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== host || $[18] !== t7) {
    t8 = createPortal(t7, host);
    $[17] = host;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function ChevronIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} fill="none" focusable="false" height="12" role="presentation" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="12"><title>Chevron</title><path d="m6 9 6 6 6-6" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function BurgerIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} fill="none" focusable="false" height="18" role="presentation" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" width="18"><title>Menu</title><path d="M4 7h16M4 12h16M4 17h10" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function CloseIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} fill="none" focusable="false" height="18" role="presentation" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" width="18"><title>Close</title><path d="M6 6l12 12M18 6 6 18" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function BrandStage(t0) {
  const $ = _c(65);
  const {
    brands,
    logoHref,
    logoAlt,
    logoSrc,
    rotateMs,
    pauseOnHover,
    showDots,
    showNavButtons,
    onIndexChange
  } = t0;
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(null);
  const [paused, setPaused] = useState(false);
  const flipping = useRef(false);
  let t1;
  if ($[0] !== brands || $[1] !== index || $[2] !== onIndexChange) {
    t1 = next => {
      if (brands.length < 2 || flipping.current) {
        return;
      }
      const n = (next + brands.length) % brands.length;
      const current = brands[index];
      if (n === index || !current) {
        return;
      }
      flipping.current = true;
      setLeaving(current);
      setIndex(n);
      onIndexChange?.(n);
    };
    $[0] = brands;
    $[1] = index;
    $[2] = onIndexChange;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const go = t1;
  let t2;
  let t3;
  if ($[4] !== onIndexChange) {
    t2 = () => {
      onIndexChange?.(0);
    };
    t3 = [onIndexChange];
    $[4] = onIndexChange;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== brands.length || $[8] !== go || $[9] !== index || $[10] !== paused || $[11] !== rotateMs) {
    t4 = () => {
      if (brands.length < 2 || rotateMs <= 0) {
        return;
      }
      const t = window.setInterval(() => {
        if (!paused) {
          go(index + 1);
        }
      }, rotateMs);
      return () => window.clearInterval(t);
    };
    t5 = [brands.length, go, index, paused, rotateMs];
    $[7] = brands.length;
    $[8] = go;
    $[9] = index;
    $[10] = paused;
    $[11] = rotateMs;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  if (brands.length === 0) {
    let t6;
    if ($[14] !== logoAlt || $[15] !== logoSrc) {
      t6 = logoSrc ? <img alt={logoAlt} className={ghh.logoImg} height={40} src={logoSrc} /> : <span className={ghh.name}>{logoAlt || "Home"}</span>;
      $[14] = logoAlt;
      $[15] = logoSrc;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== logoHref || $[18] !== t6) {
      t7 = <div className={ghh.brand}><div className={ghh.logoStage}><a className={ghh.logoCard} href={logoHref}>{t6}</a></div></div>;
      $[17] = logoHref;
      $[18] = t6;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    return t7;
  }
  const brand = brands[index] ?? brands[0];
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[20] !== brand || $[21] !== brands[0]?.id || $[22] !== leaving || $[23] !== logoAlt || $[24] !== logoHref || $[25] !== logoSrc || $[26] !== pauseOnHover || $[27] !== paused) {
    const renderCard = (item, motion) => {
      const brandSrc = item.logoUrl?.trim() || (item.id === brands[0]?.id ? logoSrc?.trim() ?? "" : "");
      return <a aria-label={`${item.name || logoAlt} home`} className={[ghh.logoCard, motion === "out" ? ghh.logoCardOut : "", motion === "in" ? ghh.logoCardIn : ""].filter(Boolean).join(" ")} href={item.href || logoHref} key={`${item.id}-${motion}`} onAnimationEnd={motion === "out" ? () => {
        setLeaving(null);
        flipping.current = false;
      } : motion === "in" ? () => {
        flipping.current = false;
      } : undefined}>{brandSrc ? <img alt={item.name || logoAlt} className={ghh.logoImg} height={40} src={brandSrc} /> : <span className={`${ghh.mark} ${item.serif ? ghh.markSerif : ""}`} style={{
          background: item.accent
        }}>{item.mono}</span>}{item.name || item.tag ? <span className={ghh.words}><span className={`${ghh.name} ${item.serif ? ghh.nameSerif : ""}`}>{item.name}</span><span className={ghh.logoTag}>{item.tag}</span></span> : null}</a>;
    };
    t10 = `${ghh.brand}${paused ? "ghh-paused" : ""}`;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setPaused(false);
        }
      };
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    if ($[38] !== pauseOnHover) {
      t12 = () => {
        if (pauseOnHover) {
          setPaused(true);
        }
      };
      t13 = () => {
        if (pauseOnHover) {
          setPaused(true);
        }
      };
      $[38] = pauseOnHover;
      $[39] = t12;
      $[40] = t13;
    } else {
      t12 = $[39];
      t13 = $[40];
    }
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = () => setPaused(false);
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    t6 = "polite";
    t7 = ghh.logoStage;
    t8 = leaving ? renderCard(leaving, "out") : null;
    t9 = renderCard(brand, leaving ? "in" : "idle");
    $[20] = brand;
    $[21] = brands[0]?.id;
    $[22] = leaving;
    $[23] = logoAlt;
    $[24] = logoHref;
    $[25] = logoSrc;
    $[26] = pauseOnHover;
    $[27] = paused;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    t10 = $[28];
    t11 = $[29];
    t12 = $[30];
    t13 = $[31];
    t14 = $[32];
    t6 = $[33];
    t7 = $[34];
    t8 = $[35];
    t9 = $[36];
  }
  let t15;
  if ($[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t15 = <div aria-live={t6} className={t7}>{t8}{t9}</div>;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== brands.length || $[48] !== go || $[49] !== index || $[50] !== showNavButtons) {
    t16 = showNavButtons && brands.length > 1 ? <div aria-label="Switch brand" className={ghh.logoNav} role="group"><button aria-label="Previous brand" onClick={() => go(index - 1)} type="button"><ChevronIcon /></button><button aria-label="Next brand" onClick={() => go(index + 1)} type="button"><ChevronIcon /></button></div> : null;
    $[47] = brands.length;
    $[48] = go;
    $[49] = index;
    $[50] = showNavButtons;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== brands || $[53] !== index || $[54] !== showDots) {
    t17 = showDots && brands.length > 1 ? <div className={ghh.brandDots}>{brands.map((b, i) => <i className={`${ghh.brandDot}${i === index ? ` ${ghh.brandDotOn}` : ""}`} key={i === index ? `${b.id}-on` : b.id} />)}</div> : null;
    $[52] = brands;
    $[53] = index;
    $[54] = showDots;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t10 || $[57] !== t11 || $[58] !== t12 || $[59] !== t13 || $[60] !== t14 || $[61] !== t15 || $[62] !== t16 || $[63] !== t17) {
    t18 = <div className={t10} onBlurCapture={t11} onFocusCapture={t12} onMouseEnter={t13} onMouseLeave={t14}>{t15}{t16}{t17}</div>;
    $[56] = t10;
    $[57] = t11;
    $[58] = t12;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  return t18;
}
export function AdvancedMegaMenuReplicaChrome({
  payload,
  afterLogo,
  drawerLead,
  previewMode = false,
  currentPathname = null,
  forcedViewport = null
}: {
  payload: AdvancedMegaMenuHeaderRuntimePayload;
  afterLogo?: ReactNode;
  drawerLead?: ReactNode;
  previewMode?: boolean;
  currentPathname?: string | null;
  forcedViewport?: ViewportMode | null;
}) {
  const group = replicaFromPayload(payload);
  const defaultNavIconPosition = group.behavior.navIconPosition;
  const forcedWidth = replicaForcedWidthPx(forcedViewport, group.breakpoints);
  const widthPx = useViewportWidth(forcedViewport, forcedWidth);
  const measuredFlags = replicaLayoutFlags(widthPx, group.breakpoints);
  const forceDesktopChrome = replicaForceDesktopPreview(previewMode, forcedViewport);
  const layoutFlags = forceDesktopChrome ? {
    asideHide: false,
    drawer: false,
    narrow: false
  } : measuredFlags;
  const navRef = useRef<HTMLElement>(null);
  const barWrapRef = useRef<HTMLDivElement>(null);
  const navOverflow = useReplicaBarOverflow({
    breakpointDrawer: layoutFlags.drawer,
    navRef,
    wrapRef: barWrapRef
  });
  const viewport: ViewportMode = layoutFlags.narrow ? "mobile" : layoutFlags.drawer || navOverflow ? "tablet" : "desktop";
  const isCompact = layoutFlags.drawer || navOverflow;
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedAcc, setExpandedAcc] = useState<string | null>(null);
  const [regionOpen, setRegionOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [brandIndex, setBrandIndex] = useState(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const indRef = useRef<HTMLSpanElement>(null);
  const rootRef = useRef<HTMLElement>(null);
  const regionBtnRef = useRef<HTMLButtonElement>(null);
  const [regionHost, setRegionHost] = useState<HTMLElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const activeBrand = group.logo.brands[brandIndex] ?? group.logo.brands[0];
  const pathLocaleCode = useMemo(() => resolveLocaleCodeFromPath(payload.languageSwitcher, currentPathname), [payload.languageSwitcher, currentPathname]);
  const [langCode, setLangCode] = useState(pathLocaleCode);
  useEffect(() => {
    setLangCode(pathLocaleCode);
  }, [pathLocaleCode]);
  useLayoutEffect(() => {
    setRegionHost(rootRef.current);
  }, []);
  const currentLocale = payload.languageSwitcher.locales.find(l => l.code === langCode) ?? payload.languageSwitcher.locales[0];
  const replicaRegions = group.utility.regionSwitcher.regions;
  const useReplicaRegion = group.utility.regionSwitcher.enabled && replicaRegions.length > 0;
  const [regionIndex, setRegionIndex] = useState(0);
  const [regionLangIndex, setRegionLangIndex] = useState(group.utility.regionSwitcher.defaultLangIndex);
  const activeRegion = replicaRegions[regionIndex] ?? replicaRegions[0];
  const stickyUi = useMegaMenuStickyHeader({
    enabled: group.behavior.sticky,
    lockVisible: activeTab != null || drawerOpen || searchOpen || regionOpen,
    mode: group.behavior.stickyMode === "smart" ? "smart" : "always",
    previewMode,
    rootRef,
    stuckAfterPx: group.behavior.compactScrollY
  });
  const stickyShell = replicaStickyShellState(group.behavior, stickyUi);
  const cssVars = applyGroupHeaderCssVars(group.design, group.behavior, group.typography, group.breakpoints, {
    accent: activeBrand?.accent,
    accentDeep: activeBrand?.accentDeep,
    accentSoft: activeBrand?.accentSoft,
    brandDotMs: group.logo.autoplayMs,
    drawerColumns: group.mobile.accordionColumns,
    drawerWidthPx: group.mobile.widthPx,
    logoHeightPx: group.logo.stageHeightPx,
    logoWidthPx: group.logo.stageWidthPx
  });
  const searchIndex = useMemo(() => buildReplicaSearchIndex(payload.roots, group.panels, group.groupMoreLinks), [group.groupMoreLinks, group.panels, payload.roots]);
  const searchLimit = group.actions.search.maxResults;
  const searchHits = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) {
      return searchIndex.slice(0, searchLimit);
    }
    return searchIndex.filter(h => `${h.label} ${h.group}`.toLowerCase().includes(q)).slice(0, searchLimit);
  }, [searchIndex, searchLimit, searchQ]);
  const moveInd = useCallback((key: string | null) => {
    const ind = indRef.current;
    const nav = navRef.current;
    if (!(ind && nav && key)) {
      if (ind) {
        ind.style.opacity = "0";
      }
      return;
    }
    const btn = nav.querySelector(`[data-panel="${key}"]`);
    if (!btn) {
      return;
    }
    const nr = nav.getBoundingClientRect();
    const br = btn.getBoundingClientRect();
    ind.style.left = `${br.left - nr.left + 12}px`;
    ind.style.width = `${Math.max(0, br.width - 24)}px`;
    ind.style.opacity = "1";
  }, []);
  useEffect(() => {
    if (!isCompact) {
      setDrawerOpen(false);
      return;
    }
    setActiveTab(null);
    moveInd(null);
  }, [isCompact, moveInd]);
  const openPanel = useCallback((id: string) => {
    if (isCompact) {
      return;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setActiveTab(id);
    moveInd(id);
  }, [isCompact, moveInd]);
  const closePanel = useCallback(() => {
    closeTimer.current = setTimeout(() => {
      setActiveTab(null);
      moveInd(null);
    }, group.behavior.closeDelayMs);
  }, [group.behavior.closeDelayMs, moveInd]);
  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        if (!group.actions.search.enabled) {
          return;
        }
        e.preventDefault();
        setSearchOpen(o => !o);
        return;
      }
      if (e.key !== "Escape") {
        return;
      }
      setActiveTab(null);
      setRegionOpen(false);
      setSearchOpen(false);
      setDrawerOpen(false);
      moveInd(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [group.actions.search.enabled, moveInd]);
  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    } else {
      setSearchQ("");
    }
  }, [searchOpen]);
  useEffect(() => {
    if (!drawerOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);
  useEffect(() => {
    const onDocClick = (e_0: MouseEvent) => {
      const root = rootRef.current;
      const target = e_0.target;
      if (root && target instanceof Node && root.contains(target)) {
        return;
      }
      if (target instanceof Element && target.closest("[data-ghh-flyout]")) {
        return;
      }
      setActiveTab(null);
      setRegionOpen(false);
      moveInd(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [moveInd]);
  const activeRoot = payload.roots.find((r): r is MegaTabRoot => r.itemKind === "mega_tab" && r.id === activeTab);
  const megaOpen = !isCompact && activeRoot != null;
  const logoHref = safeLogoHref(payload.logoHref, payload.logoUrl);
  const blockPreviewLinkNav = useCallback((e_1: ReactMouseEvent) => {
    if (!previewMode) {
      return;
    }
    const target_0 = e_1.target;
    if (!(target_0 instanceof Element)) {
      return;
    }
    if (target_0.closest("a[href]")) {
      e_1.preventDefault();
      // Keep bubbling so the builder canvas wrapper can select this element.
    }
  }, [previewMode]);
  const onNavKey = (e_2: ReactKeyboardEvent, id_0: string) => {
    if (e_2.key === "ArrowDown") {
      e_2.preventDefault();
      openPanel(id_0);
    }
  };
  const megaFlyout = <div className={`${ghh.mega} ${megaOpen ? ghh.open : ""}`} data-ghh-flyout="" data-open={megaOpen ? "true" : "false"} data-pb-mega-menu="" onMouseEnter={cancelClose} onMouseLeave={closePanel}>
			<div className={ghh.wrap}>
				{activeRoot ? <ReplicaMegaPanel badges={group.linkBadges} contact={group.actions.secondaryCta} cta={group.actions.primaryCta} layout={group.panels[activeRoot.id]} moreLinks={group.groupMoreLinks} navIcons={payload.navIcons} root={activeRoot} /> : null}
			</div>
		</div>;
  return <header className={[ghh.hdr, stickyShell.sticky ? ghh.hdrSticky : "", stickyShell.compact ? ghh.compact : "", stickyShell.retracted ? ghh.hdrRetracted : ""].filter(Boolean).join(" ")} data-chrome="replica" data-ghh-aside={layoutFlags.asideHide ? "hide" : "show"} data-ghh-drawer={isCompact ? "on" : "off"} data-ghh-glass={group.behavior.glassBackground ? "true" : "false"} data-ghh-narrow={layoutFlags.narrow ? "on" : "off"} data-ghh-region={regionOpen ? "open" : "closed"} data-ghh-sticky={stickyShell.sticky ? "true" : "false"} {...stickyShell.retracted ? {
    "data-ghh-retracted": "true"
  } : {}} {...previewMode ? {
    "data-ghh-canvas": "true"
  } : {}} {...forceDesktopChrome ? {
    "data-ghh-preview": "desktop"
  } : {}} data-pb-advanced-mega-menu="" data-pb-mega-menu="" data-viewport={viewport} onClickCapture={previewMode ? blockPreviewLinkNav : undefined} ref={rootRef} style={cssVars as CSSProperties}>
			{group.utility.enabled ? <div className={ghh.util}>
					<div className={`${ghh.wrap} ${ghh.wrap}`}>
						{group.utility.ticker.enabled ? <div className={ghh.ticker}>
								<span className={ghh.tickerDot} />
								<span>{group.utility.ticker.text}</span>
								{group.utility.ticker.linkLabel ? <a href={group.utility.ticker.href}>
										{group.utility.ticker.linkLabel}
									</a> : null}
							</div> : <span />}
						<div className={ghh.utilRight}>
							{group.utility.links.map(link => <a href={link.href} key={`${link.label}-${link.href}`}>
									{link.label}
								</a>)}
							{useReplicaRegion && activeRegion ? <div className={ghh.region}>
									<button aria-expanded={regionOpen ? "true" : "false"} aria-haspopup="dialog" className={ghh.regionBtn} onClick={e_3 => {
              if (!previewMode) {
                e_3.stopPropagation();
              }
              setRegionOpen(o_0 => !o_0);
              setActiveTab(null);
            }} ref={regionBtnRef} type="button">
										<span className={ghh.regionCode}>{activeRegion.code}</span>
										<span>
											{activeRegion.name} /{" "}
											{activeRegion.langs[regionLangIndex] ?? activeRegion.langs[0] ?? ""}
										</span>
										<ChevronIcon />
									</button>
									<RegionPopPortal ariaLabel="Choose your region and language" host={regionHost} onDialogClick={e_4 => {
              if (!previewMode) {
                e_4.stopPropagation();
              }
            }} open={regionOpen} popClassName={ghh.regionPop} portalClassName={ghh.regionPopPortal} triggerRef={regionBtnRef}>
										<div>
											<h4>Region</h4>
											<div className={ghh.regionList} role="radiogroup">
												{replicaRegions.map((region, i) => <button aria-checked={i === regionIndex} key={region.code} onClick={() => {
                    setRegionIndex(i);
                    setRegionLangIndex(0);
                  }} role="radio" type="button">
														<span className={ghh.regionCode}>
															{region.code}
														</span>
														{region.name}
													</button>)}
											</div>
										</div>
										<div>
											<h4>Language</h4>
											<div className={ghh.regionList} role="radiogroup">
												{(activeRegion.langs.length ? activeRegion.langs : ["English"]).map((lang, i_0) => <button aria-checked={i_0 === regionLangIndex} key={`${activeRegion.code}-${lang}`} onClick={() => {
                    setRegionLangIndex(i_0);
                    setRegionOpen(false);
                  }} role="radio" type="button">
														{lang}
													</button>)}
											</div>
										</div>
										<div className={ghh.regionFoot}>
											<span>
												{group.utility.regionSwitcher.currencyLabel || "Prices shown in GBP"}
											</span>
											<span>{activeRegion.timezoneNote}</span>
										</div>
									</RegionPopPortal>
								</div> : payload.languageSwitcher.enabled ? <div className={ghh.region}>
									<button aria-expanded={regionOpen ? "true" : "false"} aria-haspopup="dialog" className={ghh.regionBtn} onClick={() => {
              setRegionOpen(o_1 => !o_1);
              setActiveTab(null);
            }} ref={regionBtnRef} type="button">
										<span className={ghh.regionCode}>
											{currentLocale?.code ?? ""}
										</span>
										<span>{currentLocale?.label ?? "Region"}</span>
										<ChevronIcon />
									</button>
									<RegionPopPortal ariaLabel="Choose region and language" host={regionHost} open={regionOpen} popClassName={ghh.regionPop} portalClassName={ghh.regionPopPortal} triggerRef={regionBtnRef}>
										<div>
											<h4>Region / language</h4>
											<div className={ghh.regionList} role="radiogroup">
												{payload.languageSwitcher.locales.map(loc => {
                    const checked = loc.code === langCode;
                    const current = isLocaleCurrentForPath(loc, currentPathname);
                    return <button aria-checked={checked} disabled={current} key={loc.code} onClick={() => {
                      if (current) {
                        return;
                      }
                      setLangCode(loc.code);
                      setRegionOpen(false);
                      const href = loc.href?.trim();
                      if (!previewMode && href && href !== "#") {
                        window.location.assign(href);
                      }
                    }} role="radio" type="button">
															<span className={ghh.regionCode}>{loc.code}</span>
															{loc.label}
														</button>;
                  })}
											</div>
										</div>
									</RegionPopPortal>
								</div> : null}
						</div>
					</div>
				</div> : null}

			<div className={ghh.bar}>
				<div className={ghh.wrap} ref={barWrapRef}>
					<BrandStage brands={group.logo.enabled ? group.logo.brands : []} logoAlt={payload.logoAlt} logoHref={logoHref} logoSrc={payload.logoUrl} onIndexChange={setBrandIndex} pauseOnHover={group.logo.pauseOnHover} rotateMs={group.logo.autoplayMs} showDots={group.logo.showDots} showNavButtons={group.logo.showNavButtons} />
					{afterLogo}
					<nav aria-label="Primary" className={`${ghh.nav} ${megaOpen ? ghh.navHasActive : ""}`} onMouseEnter={cancelClose} onMouseLeave={closePanel} ref={navRef}>
						{payload.roots.map(root_0 => {
            if (root_0.itemKind === "link") {
              const rootIcon = lookupNavIcon(payload.navIcons, root_0.id);
              return <a className={ghh.navItem} href={root_0.href} key={root_0.id} onMouseEnter={() => {
                if (openTimer.current) {
                  clearTimeout(openTimer.current);
                }
                closePanel();
              }}>
										<NavIconLabel className={ghh.iconLabel} icon={rootIcon} iconClassName={ghh.navIcon} iconPosition={resolveNavIconPosition(rootIcon, defaultNavIconPosition)}>
											<span className={ghh.navLabel}>{root_0.label}</span>
										</NavIconLabel>
									</a>;
            }
            if (root_0.itemKind !== "mega_tab") {
              return null;
            }
            const count = countMegaTabEntries(root_0, group.panels[root_0.id], group.groupMoreLinks);
            const expanded = activeTab === root_0.id;
            const rootIcon_0 = lookupNavIcon(payload.navIcons, root_0.id);
            return <button aria-expanded={expanded ? "true" : "false"} className={ghh.navItem} data-panel={root_0.id} key={root_0.id} onClick={() => expanded ? closePanel() : openPanel(root_0.id)} onKeyDown={e_5 => onNavKey(e_5, root_0.id)} onMouseEnter={() => {
              if (openTimer.current) {
                clearTimeout(openTimer.current);
              }
              openTimer.current = setTimeout(() => openPanel(root_0.id), activeTab ? 0 : group.behavior.openDelayMs);
            }} type="button">
									<NavIconLabel className={ghh.iconLabel} icon={rootIcon_0} iconClassName={ghh.navIcon} iconPosition={resolveNavIconPosition(rootIcon_0, defaultNavIconPosition)}>
										<span className={ghh.navLabel}>{root_0.label}</span>
									</NavIconLabel>
									{group.behavior.showNavCounts ? <span className={ghh.navCount}>{count}</span> : null}
								</button>;
          })}
						<span className={ghh.navInd} ref={indRef} />
					</nav>
					<div className={ghh.actions}>
						{group.actions.search.enabled ? <button aria-label="Search (Ctrl K)" className={ghh.iconBtn} onClick={() => setSearchOpen(true)} type="button">
								<SearchIcon />
							</button> : null}
						{group.actions.secondaryCta.enabled ? <a className={`${ghh.btn} ${ghh.btnGhost}`} href={group.actions.secondaryCta.href}>
								{group.actions.secondaryCta.label}
							</a> : null}
						{group.actions.primaryCta.enabled ? <a className={`${ghh.btn} ${ghh.btnPrimary}`} href={group.actions.brandOverridesCta && activeBrand?.ctaHref ? activeBrand.ctaHref : group.actions.primaryCta.href}>
								{group.actions.brandOverridesCta && activeBrand?.ctaLabel ? activeBrand.ctaLabel : group.actions.primaryCta.label}
							</a> : null}
						{isCompact ? <button aria-label="Open menu" className={`${ghh.iconBtn} ${ghh.burger}`} onClick={() => setDrawerOpen(true)} type="button">
								<BurgerIcon />
							</button> : null}
					</div>
				</div>
			</div>

			{megaFlyout}

			{group.actions.search.enabled ? <div aria-label="Search the site" className={`${ghh.search} ${searchOpen ? ghh.open : ""}`} onClick={e_6 => {
      if (e_6.target === e_6.currentTarget) {
        setSearchOpen(false);
      }
    }} role="dialog">
					<div className={ghh.searchBox}>
						<div className={ghh.searchIn}>
							<SearchIcon />
							<input autoComplete="off" onChange={e_7 => setSearchQ(e_7.target.value)} placeholder={group.actions.search.placeholder} ref={searchInputRef} type="search" value={searchQ} />
							<span className={ghh.kbd}>Esc</span>
						</div>
						<div className={ghh.searchRes}>
							{searchHits.length > 0 ? searchHits.map(hit => <a href={hit.href} key={`${hit.group}-${hit.label}-${hit.href}`}>
										{hit.label}
										<span>{hit.group}</span>
									</a>) : <div className={ghh.searchEmpty}>
									{group.actions.search.emptyMessage.replace("{q}", searchQ) || `Nothing matches “${searchQ}”.`}
								</div>}
						</div>
					</div>
				</div> : null}

			{isCompact && drawerOpen ? <div className={`${ghh.drawer} ${ghh.open}`}>
					<button aria-label="Close menu" className={ghh.scrim} onClick={() => setDrawerOpen(false)} type="button" />
					<div className={ghh.sheet}>
						<div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8
        }}>
							<strong style={{
            fontSize: 17
          }}>Menu</strong>
							<button aria-label="Close menu" className={ghh.iconBtn} onClick={() => setDrawerOpen(false)} type="button">
								<CloseIcon />
							</button>
						</div>
						{drawerLead}
						<nav aria-label="Mobile primary">
							{payload.roots.map(root_1 => {
            if (root_1.itemKind === "link") {
              const rootIcon_1 = lookupNavIcon(payload.navIcons, root_1.id);
              return <a className={ghh.drawerLink} href={root_1.href} key={root_1.id} onClick={() => setDrawerOpen(false)}>
											<NavIconLabel className={ghh.iconLabel} icon={rootIcon_1} iconClassName={ghh.navIcon} iconPosition={resolveNavIconPosition(rootIcon_1, defaultNavIconPosition)}>
												{root_1.label}
											</NavIconLabel>
										</a>;
            }
            if (root_1.itemKind !== "mega_tab") {
              return null;
            }
            const open = expandedAcc === root_1.id;
            const rootIcon_2 = lookupNavIcon(payload.navIcons, root_1.id);
            return <div className={ghh.acc} key={root_1.id} {...open ? {
              open: true
            } : {}}>
										<button aria-expanded={open ? "true" : "false"} onClick={() => setExpandedAcc(cur => cur === root_1.id ? null : root_1.id)} type="button">
											<NavIconLabel className={ghh.iconLabel} icon={rootIcon_2} iconClassName={ghh.navIcon} iconPosition={resolveNavIconPosition(rootIcon_2, defaultNavIconPosition)}>
												{root_1.label}
											</NavIconLabel>
											<ChevronIcon />
										</button>
										<div className={ghh.accBody}>
											{root_1.groups.map(g => {
                  const groupIcon = lookupNavIcon(payload.navIcons, g.id);
                  return <div key={g.id}>
														<h3>
															<NavIconLabel className={ghh.iconLabel} icon={groupIcon} iconClassName={ghh.navIcon} iconPosition={resolveNavIconPosition(groupIcon, defaultNavIconPosition)}>
																{g.label}
															</NavIconLabel>
														</h3>
														{g.links.map(l_0 => {
                      const linkIcon = lookupNavIcon(payload.navIcons, l_0.id);
                      return <a href={l_0.href} key={l_0.id}>
																	<NavIconLabel className={ghh.iconLabel} icon={linkIcon} iconClassName={ghh.navIcon} iconPosition={resolveNavIconPosition(linkIcon, defaultNavIconPosition)}>
																		{l_0.label}
																	</NavIconLabel>
																</a>;
                    })}
													</div>;
                })}
										</div>
									</div>;
          })}
						</nav>
						<div className={ghh.panelFootCtas} style={{
          marginTop: 14,
          display: "grid"
        }}>
							{group.actions.primaryCta.enabled ? <a className={`${ghh.btn} ${ghh.btnPrimary}`} href={group.actions.primaryCta.href}>
									{group.actions.primaryCta.label}
								</a> : null}
							{group.actions.secondaryCta.enabled ? <a className={`${ghh.btn} ${ghh.btnGhost}`} href={group.actions.secondaryCta.href}>
									{group.actions.secondaryCta.label}
								</a> : null}
						</div>
					</div>
				</div> : null}
		</header>;
}